import { buildApiHttpError } from './api/errorHandler';

type HttpOptions = {
  searchParams?: Record<string, string | number | boolean | null | undefined>;
  json?: unknown;
  formData?: FormData;
  headers?: HeadersInit;
};

type JsonResponse = {
  json: <T>() => Promise<T>;
};

type HttpAuthHandlers = {
  refreshAccessToken?: () => Promise<string | null>;
  logout?: () => void;
};

const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
const ID_TOKEN_STORAGE_KEY = 'id_token';
const API_BASE_URL = process.env.BUN_PUBLIC_API_URL ?? '';
const DEFAULT_REQUEST_ORIGIN = 'http://localhost';

let refreshAccessTokenHandler: (() => Promise<string | null>) | null = null;
let logoutHandler: (() => void) | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let inMemoryBearerToken: string | null = null;

export const registerHttpAuthHandlers = (handlers: HttpAuthHandlers) => {
  refreshAccessTokenHandler = handlers.refreshAccessToken ?? null;
  logoutHandler = handlers.logout ?? null;
};

export const setInMemoryBearerToken = (token: string | null) => {
  inMemoryBearerToken = token;
};

const resolveRequestOrigin = () => {
  if (API_BASE_URL) {
    return API_BASE_URL;
  }

  if (typeof globalThis.location?.origin === 'string' && globalThis.location.origin.length > 0) {
    return globalThis.location.origin;
  }

  return DEFAULT_REQUEST_ORIGIN;
};

const buildUrl = (url: string, searchParams?: HttpOptions['searchParams']) => {
  const requestUrl = new URL(url, resolveRequestOrigin());

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        requestUrl.searchParams.set(key, String(value));
      }
    });
  }

  return requestUrl.toString();
};

const triggerLogout = () => {
  inMemoryBearerToken = null;
  sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  logoutHandler?.();
};

const getBearerToken = (overrideToken?: string) => {
  if (overrideToken) {
    return overrideToken;
  }

  if (inMemoryBearerToken) {
    return inMemoryBearerToken;
  }

  const idToken = sessionStorage.getItem(ID_TOKEN_STORAGE_KEY);
  const accessToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);

  return idToken || accessToken;
};

const buildHeaders = (options?: HttpOptions, overrideToken?: string) => {
  const headers = new Headers(options?.headers);
  if (!options?.formData) {
    headers.set('Content-Type', 'application/json');
  }

  const bearerToken = getBearerToken(overrideToken);

  if (bearerToken && (!headers.has('Authorization') || overrideToken)) {
    headers.set('Authorization', `Bearer ${bearerToken}`);
  }

  return headers;
};

const readPayload = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJsonResponse = contentType.includes('application/json');

  if (isJsonResponse) {
    return {
      isJsonResponse,
      payload: await response.json().catch(() => undefined),
    };
  }

  return {
    isJsonResponse,
    payload: await response.text().catch(() => ''),
  };
};

const parseResponse = async <T>(response: Response) => {
  const { isJsonResponse, payload } = await readPayload(response);

  if (!response.ok) {
    throw buildApiHttpError(
      response.status,
      `HTTP ${response.status}: ${response.statusText}`,
      payload,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (isJsonResponse) {
    return payload as T;
  }

  return payload as T;
};

const sendRequest = (
  method: string,
  url: string,
  options?: HttpOptions,
  overrideToken?: string,
) => {
  const headers = buildHeaders(options, overrideToken);

  return fetch(buildUrl(url, options?.searchParams), {
    method,
    headers,
    body:
      options?.formData ?? (options?.json !== undefined ? JSON.stringify(options.json) : undefined),
  });
};

const refreshAccessToken = async () => {
  if (!refreshAccessTokenHandler) {
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = refreshAccessTokenHandler()
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};

const request = (method: string, url: string, options?: HttpOptions): JsonResponse => ({
  json: async <T>() => {
    const response = await sendRequest(method, url, options);

    if (response.status !== 401) {
      return parseResponse<T>(response);
    }

    const refreshedToken = await refreshAccessToken();

    if (!refreshedToken) {
      triggerLogout();
      return parseResponse<T>(response);
    }

    const retryResponse = await sendRequest(method, url, options, refreshedToken);

    if (retryResponse.status === 401) {
      triggerLogout();
    }

    return parseResponse<T>(retryResponse);
  },
});

export const http = {
  get: (url: string, options?: HttpOptions) => request('GET', url, options),
  post: (url: string, options?: HttpOptions) => request('POST', url, options),
  put: (url: string, options?: HttpOptions) => request('PUT', url, options),
  patch: (url: string, options?: HttpOptions) => request('PATCH', url, options),
  delete: (url: string, options?: HttpOptions) => request('DELETE', url, options),
};
